import { supabase } from "@/integrations/supabase/client";
import { getUserIPAddress } from "../geoIpService";
import { isCurrentUserUnlimitedStream } from "../streamWhitelist";

export interface DuplicateStreamResult {
  isDuplicate: boolean;
  reason?: string;
  timeSinceLastStream?: number;
  allowanceType: "none" | "sponsored_only" | "full";
}

/**
 * Check for duplicate streams with enhanced IP-based detection
 * This implements the core IP-based daily limiting functionality
 */
export async function validateDuplicateStream(
  trackId: string,
  userId: string,
  deviceFingerprint: string = "unknown",
  ipAddress?: string,
): Promise<DuplicateStreamResult> {
  try {
    if (await isCurrentUserUnlimitedStream()) {
      console.log(
        "⭐ [DUPLICATE_VALIDATOR] Whitelisted user — bypassing duplicate check",
      );
      return { isDuplicate: false, allowanceType: "full" };
    }

    console.log("🔍 [DUPLICATE_VALIDATOR] Checking for duplicate stream:", {
      trackId,
      userId,
      deviceFingerprint: deviceFingerprint.substring(0, 8) + "...",
      ipAddress: ipAddress || "detecting...",
      timestamp: new Date().toISOString(),
    });

    // Get IP address if not provided
    const userIP = ipAddress || (await getUserIPAddress());

    if (!userIP) {
      console.warn(
        "⚠️ [DUPLICATE_VALIDATOR] No IP address available, using user-based fallback",
      );
      return await fallbackUserBasedCheck(trackId, userId);
    }

    console.log(
      "🌐 [DUPLICATE_VALIDATOR] Using IP-based duplicate detection:",
      {
        ip: userIP,
        trackId,
        userId,
      },
    );

    // RELAXED: Check how many times this IP has streamed this track in 24h (max 3 allowed)
    const { data: ipStreamCount, error } = await supabase
      .from("streams")
      .select("id")
      .eq("track_id", trackId)
      .eq("ip_address", userIP)
      .eq("is_valid", true)
      .gte(
        "created_at",
        new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      );

    if (error) {
      console.error(
        "❌ [DUPLICATE_VALIDATOR] Error checking IP-based stream count:",
        error,
      );
      return await fallbackUserBasedCheck(trackId, userId);
    }

    const streamCount = ipStreamCount?.length || 0;
    const maxAllowedPerIP = 1; // Allow 1 stream per IP per track per 24h

    if (streamCount >= maxAllowedPerIP) {
      console.log("❌ [DUPLICATE_VALIDATOR] IP stream limit reached:", {
        userId,
        trackId,
        ip: userIP,
        streamCount,
        maxAllowed: maxAllowedPerIP,
        reason: `IP has reached maximum streams (${streamCount}/${maxAllowedPerIP}) for this track within 24 hours`,
      });

      return {
        isDuplicate: true,
        reason: `This track has been streamed ${streamCount} times from your location in 24 hours (max: ${maxAllowedPerIP})`,
        allowanceType:
          streamCount >= maxAllowedPerIP ? "none" : "sponsored_only",
      };
    }

    console.log("✅ [DUPLICATE_VALIDATOR] No IP-based duplicate detected");
    return {
      isDuplicate: false,
      allowanceType: "full",
    };
  } catch (error) {
    console.error(
      "🔥 [DUPLICATE_VALIDATOR] Exception in IP-based duplicate validation:",
      error,
    );
    return await fallbackUserBasedCheck(trackId, userId);
  }
}

/**
 * Fallback duplicate check using user-based detection
 */
async function fallbackUserBasedCheck(
  trackId: string,
  userId: string,
): Promise<DuplicateStreamResult> {
  try {
    const { data: hasRecent, error } = await supabase.rpc(
      "check_duplicate_stream_enhanced",
      {
        p_track_id: trackId,
        p_user_id: userId,
      },
    );

    if (error) {
      console.error("❌ [DUPLICATE_VALIDATOR] Fallback check failed:", error);
      return {
        isDuplicate: false,
        allowanceType: "full",
      };
    }

    return {
      isDuplicate: hasRecent === true,
      reason: hasRecent
        ? "Duplicate stream detected within 24 hours (user-based fallback)"
        : undefined,
      allowanceType: hasRecent ? "none" : "full",
    };
  } catch (error) {
    console.error(
      "🔥 [DUPLICATE_VALIDATOR] Exception in fallback check:",
      error,
    );
    return {
      isDuplicate: false,
      allowanceType: "full",
    };
  }
}

/**
 * Check if user has exceeded velocity limits - RELAXED POLICY
 */
export async function checkVelocityLimits(userId: string): Promise<{
  exceeded: boolean;
  reason?: string;
  streamsInLastHour: number;
  streamsInLastDay: number;
  maxAllowedPerHour: number;
  maxAllowedPerDay: number;
}> {
  try {
    if (await isCurrentUserUnlimitedStream()) {
      console.log(
        "⭐ [VELOCITY_CHECKER] Whitelisted user — bypassing velocity check",
      );
      return {
        exceeded: false,
        streamsInLastHour: 0,
        streamsInLastDay: 0,
        maxAllowedPerHour: Number.MAX_SAFE_INTEGER,
        maxAllowedPerDay: Number.MAX_SAFE_INTEGER,
      };
    }

    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    // Check hourly streams
    const { data: hourlyStreams, error: hourlyError } = await supabase
      .from("streams")
      .select("id")
      .eq("user_id", userId)
      .eq("is_valid", true)
      .gte("created_at", oneHourAgo);

    // Check daily streams
    const { data: dailyStreams, error: dailyError } = await supabase
      .from("streams")
      .select("id")
      .eq("user_id", userId)
      .eq("is_valid", true)
      .gte("created_at", oneDayAgo);

    if (hourlyError || dailyError) {
      console.error("❌ [VELOCITY_CHECKER] Error checking velocity limits:", {
        hourlyError,
        dailyError,
      });
      return {
        exceeded: false,
        streamsInLastHour: 0,
        streamsInLastDay: 0,
        maxAllowedPerHour: 25,
        maxAllowedPerDay: 200,
      };
    }

    const hourlyCount = hourlyStreams?.length || 0;
    const dailyCount = dailyStreams?.length || 0;

    // RELAXED: Increased limits significantly
    const maxAllowedPerHour = 25; // Increased from 10 to 25
    const maxAllowedPerDay = 200; // New daily limit

    // Check hourly limit first
    if (hourlyCount >= maxAllowedPerHour) {
      console.log("🚨 [VELOCITY_CHECKER] Hourly velocity limit exceeded:", {
        userId,
        streamsInLastHour: hourlyCount,
        maxAllowedPerHour,
      });

      return {
        exceeded: true,
        reason: `Too many streams (${hourlyCount}) in the last hour. Maximum allowed: ${maxAllowedPerHour}/hour`,
        streamsInLastHour: hourlyCount,
        streamsInLastDay: dailyCount,
        maxAllowedPerHour,
        maxAllowedPerDay,
      };
    }

    // Check daily limit
    if (dailyCount >= maxAllowedPerDay) {
      console.log("🚨 [VELOCITY_CHECKER] Daily velocity limit exceeded:", {
        userId,
        streamsInLastDay: dailyCount,
        maxAllowedPerDay,
      });

      return {
        exceeded: true,
        reason: `Too many streams (${dailyCount}) in the last 24 hours. Maximum allowed: ${maxAllowedPerDay}/day`,
        streamsInLastHour: hourlyCount,
        streamsInLastDay: dailyCount,
        maxAllowedPerHour,
        maxAllowedPerDay,
      };
    }

    console.log("✅ [VELOCITY_CHECKER] Velocity limits OK:", {
      userId,
      streamsInLastHour: hourlyCount,
      streamsInLastDay: dailyCount,
      maxAllowedPerHour,
      maxAllowedPerDay,
    });

    return {
      exceeded: false,
      streamsInLastHour: hourlyCount,
      streamsInLastDay: dailyCount,
      maxAllowedPerHour,
      maxAllowedPerDay,
    };
  } catch (error) {
    console.error("🔥 [VELOCITY_CHECKER] Exception in velocity check:", error);
    return {
      exceeded: false,
      streamsInLastHour: 0,
      streamsInLastDay: 0,
      maxAllowedPerHour: 25,
      maxAllowedPerDay: 200,
    };
  }
}
